import { Functionality } from '@/types/estimation';

export const FUNCTIONALITIES: Functionality[] = [
  {
    id: 'authentication',
    name: 'Authentification',
    description: 'Système de connexion et gestion des utilisateurs',
    basePrice: 2000,
    required: true,
    subChoices: [
      { id: 'email-password', label: 'Email + Mot de passe', price: 0 },
      { id: 'phone', label: 'Téléphone', price: 500 },
      { id: 'social-media', label: 'Réseaux sociaux (Google, Facebook, Apple)', price: 1000 },
      { id: 'otp', label: 'OTP (One-Time Password)', price: 800 },
      { id: '2fa', label: 'Authentification à deux facteurs (2FA)', price: 1200 },
      { id: 'biometric', label: 'Biométrie (Face ID, Touch ID)', price: 1500 },
    ],
  },
  {
    id: 'payment',
    name: 'Paiements',
    description: 'Intégration de solutions de paiement',
    basePrice: 2000,
    subChoices: [
      { id: 'stripe', label: 'Stripe', price: 0 },
      { id: 'paypal', label: 'PayPal', price: 500 },
      { id: 'apple-pay', label: 'Apple Pay', price: 800 },
      { id: 'google-pay', label: 'Google Pay', price: 800 },
      { id: 'bank-transfer', label: 'Virement bancaire', price: 600 },
      { id: 'crypto', label: 'Cryptomonnaies', price: 1500 },
    ],
  },
  {
    id: 'invoicing',
    name: 'Facturation',
    description: 'Génération et gestion de factures',
    basePrice: 1500,
    subChoices: [
      { id: 'external-api', label: 'API externe (Stripe Invoicing, etc.)', price: 500 },
      { id: 'custom-development', label: 'Développement personnalisé', price: 2000 },
      { id: 'pdf-generation', label: 'Génération PDF personnalisée', price: 1000 },
      { id: 'email-sending', label: 'Envoi automatique par email', price: 500 },
    ],
  },
  {
    id: 'messaging',
    name: 'Messagerie',
    description: 'Système de communication entre utilisateurs',
    basePrice: 3000,
    subChoices: [
      { id: 'real-time', label: 'Temps réel (WebSocket)', price: 0 },
      { id: 'push-notifications', label: 'Notifications push', price: 800 },
      { id: 'file-sharing', label: 'Partage de fichiers', price: 1000 },
      { id: 'voice-messages', label: 'Messages vocaux', price: 1200 },
      { id: 'video-calls', label: 'Appels vidéo', price: 3000 },
    ],
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Système de notifications',
    basePrice: 1500,
    subChoices: [
      { id: 'push', label: 'Notifications push', price: 0 },
      { id: 'email', label: 'Notifications email', price: 500 },
      { id: 'sms', label: 'Notifications SMS', price: 1000 },
      { id: 'in-app', label: 'Notifications in-app', price: 0 },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Suivi et analyse des données',
    basePrice: 2000,
    subChoices: [
      { id: 'google-analytics', label: 'Google Analytics', price: 0 },
      { id: 'custom-dashboard', label: 'Tableau de bord personnalisé', price: 2500 },
      { id: 'user-tracking', label: 'Suivi utilisateur avancé', price: 1500 },
      { id: 'reports', label: 'Rapports automatisés', price: 1000 },
    ],
  },
  {
    id: 'geolocation',
    name: 'Géolocalisation',
    description: 'Fonctionnalités basées sur la localisation',
    basePrice: 2000,
    subChoices: [
      { id: 'basic', label: 'Géolocalisation basique', price: 0 },
      { id: 'maps', label: 'Intégration cartes (Google Maps, Mapbox)', price: 1000 },
      { id: 'tracking', label: 'Suivi en temps réel', price: 2000 },
      { id: 'geofencing', label: 'Géofencing (zones virtuelles)', price: 1500 },
    ],
  },
  {
    id: 'search',
    name: 'Recherche',
    description: 'Système de recherche avancée',
    basePrice: 1500,
    subChoices: [
      { id: 'basic', label: 'Recherche basique', price: 0 },
      { id: 'advanced-filters', label: 'Filtres avancés', price: 1000 },
      { id: 'full-text', label: 'Recherche full-text', price: 1500 },
      { id: 'ai-search', label: 'Recherche IA', price: 3000 },
    ],
  },
  {
    id: 'admin-panel',
    name: 'Panneau d\'administration',
    description: 'Interface d\'administration complète',
    basePrice: 3000,
    subChoices: [
      { id: 'basic', label: 'Panneau basique', price: 0 },
      { id: 'user-management', label: 'Gestion utilisateurs avancée', price: 1500 },
      { id: 'content-management', label: 'Gestion de contenu', price: 2000 },
      { id: 'analytics-dashboard', label: 'Tableau de bord analytics', price: 2500 },
    ],
  },
  {
    id: 'file-upload',
    name: 'Upload de fichiers',
    description: 'Gestion de téléchargement de fichiers',
    basePrice: 1000,
    subChoices: [
      { id: 'basic', label: 'Upload basique', price: 0 },
      { id: 'cloud-storage', label: 'Stockage cloud (AWS S3, etc.)', price: 1500 },
      { id: 'image-processing', label: 'Traitement d\'images', price: 2000 },
      { id: 'video-processing', label: 'Traitement vidéo', price: 3000 },
    ],
  },
];

// Get functionalities based on revenue models and platforms
export function getRecommendedFunctionalities(
  revenueModels: string[],
  platforms: string[]
): string[] {
  const recommended: string[] = ['authentication']; // Always include auth

  if (revenueModels.includes('subscription') || revenueModels.includes('in-app-purchase') || 
      revenueModels.includes('commission') || revenueModels.includes('one-time-payment')) {
    recommended.push('payment');
    recommended.push('invoicing');
  }

  if (revenueModels.includes('commission')) {
    recommended.push('messaging');
  }

  if (revenueModels.includes('ads')) {
    recommended.push('analytics');
  }

  if (platforms.includes('mobile') || platforms.includes('tablet') || platforms.includes('watch')) {
    recommended.push('notifications');
  }

  return recommended;
}

